'use client';

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { companyFormFields } from '@eliadmin/core/src/api/company-autocomplete/company-autocomplete-utils';
import { useCompanyLookup } from '@eliadmin/core/src/hooks/use-company-autocomplete';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { getCompanyAutocompleteConfig } from '@eliadmin/core/src/server/company-autocomplete-actions';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { joinStreet } from '@eliadmin/core/src/utils/nt/address';

/*
 * Messages of the core company autocomplete (`CompanyAutocompleteField`) – the core reads them with
 * these literal keys; the dictionary has no `textNNN` for them.
 */
const LOOKUP_FAILED = 'Firemné údaje sa nepodarilo načítať. Vyplňte ich ručne.';
const LOOKUP_DONE = 'Skontrolujte fakturačnú adresu, DIČ a IČ DPH. Chýbajúce údaje doplňte ručne.';

/** The company fields a lookup fills – the checkout and the registration name them alike. */
type CompanyFields = Record<
  'company' | 'ico' | 'dic' | 'ic_dph' | 'address' | 'city' | 'postcode',
  string | undefined
>;

/**
 * The store's company register (`/store/api-keys` → `company_autocomplete`, read by the core server
 * action). Without a provider there is nothing to look up: no „Vyhľadať“ and no promise in the hint.
 */
function useLookupAvailable() {
  const { data } = useQuery({
    queryKey: ['nt-company-autocomplete-config'],
    queryFn: () => getCompanyAutocompleteConfig(),
    staleTime: Infinity,
    retry: false,
  });
  return !!data?.provider;
}

/**
 * „Vyhľadať“ of the IČO field (checkout 172:14847, registration 221:13989): the core lookup by IČO
 * (exact match only) fills the company name, DIČ, IČ DPH and the seat of the surrounding form; the
 * customer checks and completes them. `message` belongs to the IČO it was looked up for – a changed
 * IČO shows the field's hint again.
 */
export function useIcoLookup() {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const { control, getValues, setValue } = useFormContext<CompanyFields>();
  const ico = useWatch({ control, name: 'ico' }) ?? '';
  const available = useLookupAvailable();
  const { lookupIco, isLoading } = useCompanyLookup(locale);
  const [result, setResult] = useState<{ ico: string; status: 'done' | 'failed' } | null>(null);

  const lookup = async () => {
    const typed = getValues('ico') ?? '';
    setResult(null);
    try {
      const found = await lookupIco(typed);
      if (!found) return; // replaced by a newer lookup
      if (!found.data) {
        setResult({ ico: typed, status: 'failed' });
        return;
      }
      const fields = companyFormFields(found.data);
      const fill = (name: keyof CompanyFields, value?: string) => {
        if (value) setValue(name, value, { shouldDirty: true, shouldValidate: true });
      };
      fill('ico', fields.ico);
      fill('company', fields.company);
      fill('dic', fields.dic);
      fill('ic_dph', fields.ic_dph);
      fill('address', joinStreet(fields.address, fields.street_number));
      fill('city', fields.city);
      fill('postcode', fields.postcode);
      setResult({ ico: fields.ico ?? typed, status: 'done' });
    } catch {
      // Invalid IČO (not 8 digits), provider down or rate-limited: the customer types the data.
      setResult({ ico: typed, status: 'failed' });
    }
  };

  const status = result?.ico === ico ? result.status : null;
  const message = status === 'done' ? t(LOOKUP_DONE) : status === 'failed' ? t(LOOKUP_FAILED) : '';

  return { available, isLoading, lookup, message };
}
